import { ImageUploadConfig } from '../config/imageUploadConfig';

export type ValidationSeverity = 'BLOCKING' | 'WARNING';
export type ValidationErrorCode =
  | 'FILE_TOO_LARGE'
  | 'DIMENSIONS_TOO_SMALL'
  | 'DIMENSIONS_TOO_LARGE'
  | 'ASPECT_RATIO_MISMATCH'
  | 'UNSUPPORTED_FORMAT'
  | 'TARGET_SIZE_UNREACHABLE_WITHOUT_QUALITY_LOSS'
  | 'FILE_READ_ERROR';

export interface ValidationIssue {
  severity: ValidationSeverity;
  code: ValidationErrorCode;
  message: string;
  actual?: string | number;
  expected?: string | number;
}

export interface ValidationResult {
  isValid: boolean;
  issues: ValidationIssue[];
  width: number;
  height: number;
  previewUrl: string;
}

export const validateImageDimensions = (
  width: number,
  height: number,
  config: ImageUploadConfig
): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];

  if (width < config.minWidth || height < config.minHeight) {
    issues.push({
      severity: 'BLOCKING',
      code: 'DIMENSIONS_TOO_SMALL',
      message: `Minimum required dimensions are ${config.minWidth} × ${config.minHeight} px.`,
      actual: `${width}x${height}`,
      expected: `${config.minWidth}x${config.minHeight}`,
    });
  }

  const actualRatio = width / height;
  const ratioDiff = Math.abs(actualRatio - config.targetRatio) / config.targetRatio;
  // Avoid rejecting an image exactly on the tolerance boundary because of
  // floating-point representation (for example, 1.03 versus a 3% limit).
  if (ratioDiff > config.ratioTolerance + 1e-10) {
    const target = config.targetRatio === 1 ? '1:1' : `${config.targetRatio.toFixed(2)}:1`;
    issues.push({
      severity: 'BLOCKING',
      code: 'ASPECT_RATIO_MISMATCH',
      message: `Incorrect aspect ratio. Requires approximately ${target}.`,
      actual: actualRatio.toFixed(2),
      expected: config.targetRatio.toFixed(2),
    });
  }

  if (
    issues.every(issue => issue.severity !== 'BLOCKING') &&
    (width > config.recommendedWidth * 1.5 || height > config.recommendedHeight * 1.5)
  ) {
    issues.push({
      severity: 'WARNING',
      code: 'DIMENSIONS_TOO_LARGE',
      message: `Image is larger than recommended (${config.recommendedWidth} × ${config.recommendedHeight} px), but will be accepted.`,
      actual: `${width}x${height}`,
      expected: `${config.recommendedWidth}x${config.recommendedHeight}`,
    });
  }

  return issues;
};

export const validateImageFile = (
  file: File,
  config: ImageUploadConfig
): Promise<ValidationResult> =>
  new Promise(resolve => {
    const issues: ValidationIssue[] = [];

    if (!config.allowedFormats.includes(file.type)) {
      issues.push({
        severity: 'BLOCKING',
        code: 'UNSUPPORTED_FORMAT',
        message: `Unsupported format. Allowed: ${config.allowedFormats.map(format => format.replace('image/', '')).join(', ')}`,
        actual: file.type,
        expected: config.allowedFormats.join(', '),
      });
    }

    const sizeKb = file.size / 1024;
    if (sizeKb > config.maxFilesizeKb) {
      issues.push({
        severity: 'BLOCKING',
        code: 'FILE_TOO_LARGE',
        message: `File size (${Math.round(sizeKb)} KB) exceeds the limit of ${config.maxFilesizeKb} KB.`,
        actual: Math.round(sizeKb),
        expected: config.maxFilesizeKb,
      });
    }

    const reader = new FileReader();
    reader.onload = event => {
      const dataUrl = event.target?.result as string;
      const image = new Image();
      image.onload = () => {
        const width = image.naturalWidth;
        const height = image.naturalHeight;
        issues.push(...validateImageDimensions(width, height, config));

        if (sizeKb > config.maxFilesizeKb * 0.8 && sizeKb <= config.maxFilesizeKb) {
          issues.push({
            severity: 'WARNING',
            code: 'FILE_TOO_LARGE',
            message: `File size (${Math.round(sizeKb)} KB) is high but within limits.`,
            actual: Math.round(sizeKb),
            expected: config.maxFilesizeKb,
          });
        }

        resolve({
          isValid: issues.every(issue => issue.severity !== 'BLOCKING'),
          issues,
          width,
          height,
          previewUrl: dataUrl,
        });
      };
      image.onerror = () => resolve({
        isValid: false,
        issues: [...issues, { severity: 'BLOCKING', code: 'FILE_READ_ERROR', message: 'Failed to read image data.' }],
        width: 0,
        height: 0,
        previewUrl: '',
      });
      image.src = dataUrl;
    };
    reader.onerror = () => resolve({
      isValid: false,
      issues: [...issues, { severity: 'BLOCKING', code: 'FILE_READ_ERROR', message: 'Failed to read file.' }],
      width: 0,
      height: 0,
      previewUrl: '',
    });
    reader.readAsDataURL(file);
  });
